import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TypedMemo } from '../../../common/types';
import { SMLLink } from '../../components/SMLLink/SMLLink';
import { SMLNavItem } from '../smlNavigation';
import { SMLNavBadge } from '../hooks/useNavBadges';
import { SMLNavUsageCaption } from '../hooks/useNavUsageCaptions';
import { NavBadge } from '../NavBadge';
import { LockChip } from '../../components/LockedControl/LockedControl';
import styles from './MobileHubTile.module.scss';

interface MobileHubTileProps {
  item: SMLNavItem;
  isActive: boolean;
  badge?: SMLNavBadge;
  usage?: SMLNavUsageCaption;
  params?: Record<string, string>;
  onNavigate: () => void;
}

/**
 * One destination tile of a hub sheet (design b2, b3, b6): icon, name, badge, the one-line text
 * and, on Free, the usage caption. At the limit the tile shows the lock chip of the cheapest plan,
 * but it still opens the page — no destination is locked in the navigation (design §6).
 */
const MobileHubTileComponent: React.FC<MobileHubTileProps> = ({ item, isActive, badge, usage, params, onNavigate }) => {
  const intl = useIntl();
  const lockFeature = usage?.lockFeature;

  return (
    <li>
      <SMLLink
        to={item.route}
        params={params}
        className={clsx(styles.tile, isActive && styles.active)}
        ariaCurrent={isActive ? 'page' : undefined}
        onNavigate={onNavigate}
      >
        <span className={styles.top}>
          <span className={styles.iconBox} aria-hidden="true">
            <FontAwesomeIcon icon={item.icon} fixedWidth />
          </span>
          <b className={styles.name}>{intl.formatMessage(item.label)}</b>
          {badge !== undefined && <NavBadge badge={badge} className={styles.end} />}
          {lockFeature !== undefined && <LockChip feature={lockFeature} className={styles.end} withSpokenHint />}
        </span>
        {item.description !== undefined && (
          <small className={styles.description}>{intl.formatMessage(item.description)}</small>
        )}
        {usage !== undefined && (
          <small className={clsx(styles.usage, lockFeature !== undefined && styles.usageFull)}>{usage.text}</small>
        )}
      </SMLLink>
    </li>
  );
};

export const MobileHubTile = TypedMemo(MobileHubTileComponent);
